import { useRef, useState } from "react";
import type { OrgKind, QuoteRequestDraft } from "../shared/types.js";
import { FormField } from "../components/FormField.js";
import { FileUpload, type UploadItem } from "./FileUpload.js";
import { SuccessState, ErrorState, ValidationMessage } from "../components/States.js";
import { required, isEmail, isBelgianPhone, isBelgianVat, isBelgianPostalCode, passesSpamHeuristics } from "../shared/validate.js";
import { BuildingIcon, BriefcaseIcon, CheckIcon } from "../components/Icons.js";
import { trackEvent } from "../client/analytics.js";

const SERVICES = [
  "Asbestattest gemene delen",
  "Meerdere individuele attesten",
  "Codex-inventaris",
  "Beheersprogramma",
  "Destructieve inventaris",
  "Sloopopvolgingsplan",
  "Aanvullend onderzoek",
  "Advies",
  "Andere opdracht",
];

function emptyDraft(defaultOrgKind: OrgKind): QuoteRequestDraft {
  return {
    orgKind: defaultOrgKind,
    orgName: "",
    vatNumber: "",
    contactPerson: "",
    role: "",
    email: "",
    phone: "",
    billingInfo: "",
    preferredContact: "email",
    building: {
      address: "",
      postalCode: "",
      city: "",
      buildingType: "",
      hasElevator: false,
      hasBasement: false,
      hasGarages: false,
      technicalRooms: false,
      inUse: true,
    },
    services: [],
    message: "",
  };
}

function validate(draft: QuoteRequestDraft): Record<string, string> {
  const errors: Record<string, string> = {};
  const set = (field: string, r: { valid: boolean; message?: string }) => {
    if (!r.valid) errors[field] = r.message!;
  };
  set("orgName", required(draft.orgName, "Naam organisatie"));
  set("contactPerson", required(draft.contactPerson, "Contactpersoon"));
  set("email", isEmail(draft.email));
  set("phone", isBelgianPhone(draft.phone));
  if (draft.vatNumber) set("vatNumber", isBelgianVat(draft.vatNumber));
  set("address", required(draft.building.address, "Adres"));
  set("postalCode", isBelgianPostalCode(draft.building.postalCode));
  set("city", required(draft.building.city, "Gemeente"));
  set("buildingType", required(draft.building.buildingType, "Type gebouw"));
  return errors;
}

export function QuoteForm({ defaultOrgKind = "vme" as OrgKind }: { defaultOrgKind?: OrgKind }) {
  const [draft, setDraft] = useState<QuoteRequestDraft>(() => emptyDraft(defaultOrgKind));
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [files, setFiles] = useState<UploadItem[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; reference?: string; error?: string } | null>(null);
  const renderedAt = useRef(Date.now());

  function patch(p: Partial<QuoteRequestDraft>) {
    setDraft((d) => ({ ...d, ...p }));
  }
  function patchBuilding(p: Partial<QuoteRequestDraft["building"]>) {
    setDraft((d) => ({ ...d, building: { ...d.building, ...p } }));
  }
  function toggleService(service: string) {
    setDraft((d) => ({
      ...d,
      services: d.services.includes(service) ? d.services.filter((s) => s !== service) : [...d.services, service],
    }));
  }

  const errors = validate(draft);
  const err = (field: string) => (touched[field] ? errors[field] : undefined);

  async function submit(e: any) {
    e.preventDefault();
    setTouched(Object.fromEntries(Object.keys(errors).map((k) => [k, true])));
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    setResult(null);
    try {
      const form = new FormData();
      form.set(
        "payload",
        JSON.stringify({
          ...draft,
          honeypot: (document.getElementById("quote-website") as HTMLInputElement | null)?.value || "",
          formRenderedAt: renderedAt.current,
        })
      );
      for (const item of files) {
        if (!item.error) form.append("files", item.file, item.file.name);
      }
      const csrf = document.cookie.match(/(?:^|; )aag_csrf=([^;]*)/)?.[1] || "";
      const res = await fetch("/api/quote", { method: "POST", body: form, headers: { "X-CSRF-Token": csrf }, credentials: "same-origin" });
      const data = await res.json();
      if (data.ok) {
        trackEvent("quote_form_submitted", { reference: data.data.reference });
        window.location.href = `/offerte-bevestigd?ref=${encodeURIComponent(data.data.reference)}`;
        return;
      } else {
        setResult({ ok: false, error: data.error });
      }
    } catch {
      setResult({ ok: false, error: "Er ging iets mis bij het verzenden. Probeer het opnieuw." });
    } finally {
      setSubmitting(false);
    }
  }

  if (result?.ok) {
    return (
      <SuccessState title="Uw offerteaanvraag is verzonden!">
        <p>
          Referentie: <strong>{result.reference}</strong>. We reageren binnen 1 werkdag met een voorstel op maat.
        </p>
      </SuccessState>
    );
  }

  return (
    <form onSubmit={submit} noValidate>
      <div className="option-grid" style={{ gridTemplateColumns: "repeat(2, 1fr)", maxWidth: 480 }} role="group" aria-label="Type aanvrager">
        <button type="button" className="option-card option-card--wide" aria-pressed={draft.orgKind === "vme"} onClick={() => patch({ orgKind: "vme" })}>
          <span className="option-card__check">
            <CheckIcon size={12} />
          </span>
          <BuildingIcon size={22} /> VME / Syndicus
        </button>
        <button type="button" className="option-card option-card--wide" aria-pressed={draft.orgKind === "bedrijf"} onClick={() => patch({ orgKind: "bedrijf" })}>
          <span className="option-card__check">
            <CheckIcon size={12} />
          </span>
          <BriefcaseIcon size={22} /> Bedrijf
        </button>
      </div>

      <h3 className="mt-6">Algemene gegevens</h3>
      <div className="form-row">
        <FormField id="orgName" label="Naam organisatie / VME" required value={draft.orgName} onChange={(v) => patch({ orgName: v })} onBlur={() => setTouched((t) => ({ ...t, orgName: true }))} error={err("orgName")} placeholder="bv. Residentie De Marken" />
        <FormField id="vatNumber" label="Ondernemingsnummer" value={draft.vatNumber || ""} onChange={(v) => patch({ vatNumber: v })} onBlur={() => setTouched((t) => ({ ...t, vatNumber: true }))} error={err("vatNumber")} placeholder="BE0123456789" />
      </div>
      <div className="form-row">
        <FormField id="contactPerson" label="Contactpersoon" required value={draft.contactPerson} onChange={(v) => patch({ contactPerson: v })} onBlur={() => setTouched((t) => ({ ...t, contactPerson: true }))} error={err("contactPerson")} />
        <FormField id="role" label="Functie" value={draft.role || ""} onChange={(v) => patch({ role: v })} />
      </div>
      <div className="form-row">
        <FormField id="email" label="E-mailadres" type="email" required value={draft.email} onChange={(v) => patch({ email: v })} onBlur={() => setTouched((t) => ({ ...t, email: true }))} error={err("email")} autoComplete="email" />
        <FormField id="phone" label="Telefoonnummer" type="tel" required value={draft.phone} onChange={(v) => patch({ phone: v })} onBlur={() => setTouched((t) => ({ ...t, phone: true }))} error={err("phone")} autoComplete="tel" inputMode="tel" />
      </div>
      <FormField id="billingInfo" label="Facturatiegegevens (optioneel)" as="textarea" rows={2} value={draft.billingInfo || ""} onChange={(v) => patch({ billingInfo: v })} />
      <div className="form-field">
        <label htmlFor="preferredContact">Voorkeurswijze van contact</label>
        <select id="preferredContact" className="form-select" value={draft.preferredContact} onChange={(e: any) => patch({ preferredContact: e.target.value })}>
          <option value="email">E-mail</option>
          <option value="telefoon">Telefoon</option>
        </select>
      </div>

      <h3 className="mt-6">Gebouwgegevens</h3>
      <div className="form-row">
        <FormField id="address" label="Adres" required value={draft.building.address} onChange={(v) => patchBuilding({ address: v })} onBlur={() => setTouched((t) => ({ ...t, address: true }))} error={err("address")} />
        <FormField id="buildingType" label="Type gebouw" required value={draft.building.buildingType} onChange={(v) => patchBuilding({ buildingType: v })} onBlur={() => setTouched((t) => ({ ...t, buildingType: true }))} error={err("buildingType")} placeholder="bv. appartementsgebouw" />
      </div>
      <div className="form-row">
        <FormField id="postalCode" label="Postcode" required value={draft.building.postalCode} onChange={(v) => patchBuilding({ postalCode: v })} onBlur={() => setTouched((t) => ({ ...t, postalCode: true }))} error={err("postalCode")} autoComplete="postal-code" inputMode="numeric" />
        <FormField id="city" label="Gemeente" required value={draft.building.city} onChange={(v) => patchBuilding({ city: v })} onBlur={() => setTouched((t) => ({ ...t, city: true }))} error={err("city")} autoComplete="address-level2" />
      </div>
      <div className="form-row">
        <FormField id="buildYear" label="Bouwjaar" type="number" inputMode="numeric" value={String(draft.building.buildYear || "")} onChange={(v) => patchBuilding({ buildYear: Number(v) || undefined })} />
        <FormField id="floors" label="Aantal verdiepingen" type="number" inputMode="numeric" value={String(draft.building.floors || "")} onChange={(v) => patchBuilding({ floors: Number(v) || undefined })} />
      </div>
      <div className="form-row">
        <FormField id="entrances" label="Aantal ingangen" type="number" inputMode="numeric" value={String(draft.building.entrances || "")} onChange={(v) => patchBuilding({ entrances: Number(v) || undefined })} />
        <FormField id="stairwells" label="Aantal traphallen" type="number" inputMode="numeric" value={String(draft.building.stairwells || "")} onChange={(v) => patchBuilding({ stairwells: Number(v) || undefined })} />
      </div>
      <div className="form-row">
        <FormField id="corridors" label="Aantal gangen" type="number" inputMode="numeric" value={String(draft.building.corridors || "")} onChange={(v) => patchBuilding({ corridors: Number(v) || undefined })} />
        <FormField id="privateUnits" label="Aantal privatieve eenheden" type="number" inputMode="numeric" value={String(draft.building.privateUnits || "")} onChange={(v) => patchBuilding({ privateUnits: Number(v) || undefined })} />
      </div>
      <div className="form-row">
        <FormField id="surfaceM2" label="Geschatte oppervlakte (m²)" type="number" inputMode="numeric" value={String(draft.building.surfaceM2 || "")} onChange={(v) => patchBuilding({ surfaceM2: Number(v) || undefined })} />
        <div />
      </div>

      <div className="grid grid-2 mt-2 mb-4">
        {[
          { key: "hasElevator" as const, label: "Lift aanwezig?" },
          { key: "hasBasement" as const, label: "Kelder aanwezig?" },
          { key: "hasGarages" as const, label: "Garages aanwezig?" },
          { key: "technicalRooms" as const, label: "Technische lokalen aanwezig?" },
        ].map((f) => (
          <div className="form-field mb-0" key={f.key}>
            <label htmlFor={f.key}>{f.label}</label>
            <select id={f.key} className="form-select" value={draft.building[f.key] ? "ja" : "nee"} onChange={(e: any) => patchBuilding({ [f.key]: e.target.value === "ja" } as any)}>
              <option value="">Maak een keuze</option>
              <option value="ja">Ja</option>
              <option value="nee">Nee</option>
            </select>
          </div>
        ))}
      </div>

      <h3 className="mt-6">Gewenste dienst</h3>
      <div className="grid grid-3">
        {SERVICES.map((s) => (
          <label key={s} className="checkbox-field">
            <input type="checkbox" checked={draft.services.includes(s)} onChange={() => toggleService(s)} />
            {s}
          </label>
        ))}
      </div>

      <FormField id="message" label="Extra informatie / bericht" as="textarea" rows={4} value={draft.message || ""} onChange={(v) => patch({ message: v })} />

      <h3 className="mt-6">Plannen of documenten toevoegen (optioneel)</h3>
      <FileUpload items={files} onChange={setFiles} />

      <div className="hp-field" aria-hidden="true">
        <label htmlFor="quote-website">Website (niet invullen)</label>
        <input type="text" id="quote-website" name="quote-website" tabIndex={-1} autoComplete="off" />
      </div>

      {result && !result.ok && <ValidationMessage>{result.error}</ValidationMessage>}

      <button type="submit" className="btn btn-primary btn-lg mt-6" disabled={submitting}>
        {submitting ? "Bezig met verzenden…" : "Offerte aanvragen"}
      </button>
    </form>
  );
}
